import axios from 'axios';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseDropdownOnBlur,
    setCaptchaEuScript,
    normalizeModelName,
} from '../utils';
import { getFormRenderToken } from '../services/anti-bot';
import { isCaptchaEnabled } from '../services/captcha';

Vue.component('competition-form-component', {
    props: {
        distributors: {
            value: Array,
        },
        siteHandle: {
            value: String,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            successMessage: '',
            formCompleted: false,
            submitting: false,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: '',

            internalCountryHandle: '',
            isNumberValid: true,

            newsletterSignUp: false,
        };
    },

    computed: {
        isSubmitLoading: function () {
            return this.submitting ? 'button--loading' : '';
        },

        isCountryEurope: function () {
            return this.siteHandle === 'default';
        },
    },

    watch: {
        selectedDate() {
            if (this.selectedDate) {
                this.selectDate();
            }
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.countryHandle = this.siteHandle;

        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseDropdownOnBlur('.new-input__dropdown-container--model');
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleToggleButtonActiveClass(index) {
            return this.requestIndex === index ? 'checked' : '';
        },

        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            this.newsletterSignUp = isConsentChecked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            this.successMessage = '';
            const form = event.currentTarget;
            let formData = new FormData(form);
            const data = {};

            for (let [key, val] of formData.entries()) {
                Object.assign(data, { [key]: val });
            }

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Finance Quote',
                client_type: 'B2C',
                brandstore: '',
                payment_type: '',
                car: {
                    name: data.model ? normalizeModelName(data.model) : '',
                    ids: [],
                    edition: '',
                    color: '',
                },
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: '',
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };

            if (!this.isNumberValid) {
                this.submitting = false;
                return;
            }

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(form, dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    dataLayerData,
                    captchaSolution,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, dataLayerData, captchaSolution) {
            try {
                const formData = new FormData(form);
                formData.append('formRenderToken', this.formRenderToken);

                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                const response = await axios.post(
                    '/competition-form/create',
                    formData,
                );
                const result = response.data;

                if (result.data?.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                // Success
                this.formCompleted = true;
                this.errors.clear();
                this.isNumberValid = true;

                this.sendGTMData(dataLayerData);

                setTimeout(() => {
                    window.location = result.data.redirectUrl;
                }, 1000);
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(extraDataToPush) {
            const country = this.siteHandle;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                if (extraDataToPush) {
                    window.dataLayer.push(extraDataToPush);
                }

                window.dataLayer.push({
                    event: 'configuration',
                    model: extraDataToPush.car.name,
                    edition: ' ',
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                });
            }
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },
    },
});
